<template>
  <div class="qd" :class="{ 'is-sent': sent }">
    <header class="qd__head">
      <span class="qd__badge">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></svg>
        Quotation request
      </span>
      <span v-if="sent" class="qd__sent">Sent · {{ sent.reference }}</span>
      <span v-else class="qd__hint">Prepared from what you told me — check it, then press Send.</span>
    </header>

    <div v-if="sent" class="qd__summary">
      <strong>{{ form.title }}</strong>
      <p>{{ form.description }}</p>
      <router-link v-if="signedIn" to="/my-requests" class="qd__link" @click="$emit('navigate')">See my requests →</router-link>
    </div>

    <form v-else class="qd__form" @submit.prevent="submit">
      <label class="qd__field qd__field--wide">
        <span>What is it?</span>
        <input v-model="form.title" type="text" maxlength="191" required :disabled="busy" />
      </label>
      <label class="qd__field">
        <span>Type</span>
        <select v-model="form.item_type" :disabled="busy">
          <option v-for="t in TYPES" :key="t.value" :value="t.value">{{ t.label }}</option>
        </select>
      </label>
      <label class="qd__field qd__field--small">
        <span>Quantity</span>
        <input v-model.number="form.quantity" type="number" min="1" max="10000" :disabled="busy" />
      </label>

      <div class="qd__field qd__field--wide">
        <span>Size ({{ form.unit }}) — leave empty if you are not sure</span>
        <div class="qd__dims">
          <input v-model.number="form.width" type="number" min="0" step="0.1" placeholder="Width" :disabled="busy" />
          <input v-model.number="form.depth" type="number" min="0" step="0.1" placeholder="Depth" :disabled="busy" />
          <input v-model.number="form.height" type="number" min="0" step="0.1" placeholder="Height" :disabled="busy" />
          <select v-model="form.unit" :disabled="busy"><option value="cm">cm</option><option value="mm">mm</option><option value="in">in</option></select>
        </div>
      </div>
      <label class="qd__field">
        <span>Material / wood</span>
        <input v-model="form.material" type="text" maxlength="191" placeholder="e.g. walnut" :disabled="busy" />
      </label>
      <label class="qd__field">
        <span>Fabric / finish</span>
        <input v-model="form.fabric" type="text" maxlength="191" placeholder="e.g. green velvet" :disabled="busy" />
      </label>
      <label class="qd__field qd__field--wide">
        <span>Details</span>
        <textarea v-model="form.description" rows="4" maxlength="5000" required :disabled="busy"></textarea>
      </label>

      <div class="qd__field qd__field--wide">
        <span>Photos for our team (optional) — {{ draft.photos?.length ? 'tick the ones to include, or ' : '' }}add your own</span>
        <div class="qd__photos">
          <label v-for="p in draft.photos" :key="p.path" class="qd__photo" :class="{ 'is-on': form.photos.includes(p.path) }" :title="form.photos.includes(p.path) ? 'Included — click to leave it out' : 'Click to include'">
            <input v-model="form.photos" type="checkbox" :value="p.path" :disabled="busy" />
            <img :src="p.url" :alt="p.name" />
            <em v-if="form.photos.includes(p.path)">✓</em>
          </label>
          <div v-for="(f, i) in uploads" :key="f.key" class="qd__photo is-on is-upload">
            <img :src="f.url" :alt="f.name" />
            <button type="button" class="qd__photo-remove" :aria-label="`Remove ${f.name}`" :disabled="busy" @click="removeUpload(i)">×</button>
          </div>
          <label v-if="uploads.length < 3" class="qd__add" :class="{ 'is-disabled': busy }">
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple :disabled="busy" @change="onPick" />
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14" /></svg>
            <span>Add photo</span>
          </label>
        </div>
        <small class="qd__photo-hint">A photo of the room, a sketch, or a piece you like. JPG/PNG/WEBP, up to 6 MB each.</small>
      </div>

      <p v-if="draft.missing?.length" class="qd__missing qd__field--wide">
        It would help us to know: {{ draft.missing.join(' · ') }} — add it above if you can.
      </p>

      <template v-if="!signedIn">
        <div class="qd__field qd__field--wide qd__contact-title">How can we reach you?</div>
        <label class="qd__field"><span>Name</span><input v-model="form.contact_name" type="text" maxlength="191" required autocomplete="name" :disabled="busy" /></label>
        <label class="qd__field"><span>Phone / WhatsApp</span><input v-model="form.contact_phone" type="tel" maxlength="50" required autocomplete="tel" :disabled="busy" /></label>
        <label class="qd__field qd__field--wide"><span>Email</span><input v-model="form.contact_email" type="email" maxlength="191" required autocomplete="email" :disabled="busy" /></label>
      </template>

      <p v-if="error" class="qd__error qd__field--wide">{{ error }}</p>

      <div class="qd__actions qd__field--wide">
        <span>We reply with an invoice within two working days.</span>
        <button type="submit" class="qd__send" :disabled="busy">
          <svg v-if="!busy" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" /></svg>
          {{ busy ? 'Sending…' : 'Send request' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, onBeforeUnmount, reactive, ref } from 'vue';

const props = defineProps({
  message: { type: Object, required: true },
  draft: { type: Object, required: true },
  signedIn: { type: Boolean, default: false },
  user: { type: Object, default: null },
  send: { type: Function, required: true }, // (message, payload) => Promise
});
defineEmits(['navigate']);

const TYPES = [
  { value: 'seating', label: 'Sofas & seating' },
  { value: 'tables', label: 'Tables & desks' },
  { value: 'bedroom', label: 'Beds & bedroom' },
  { value: 'storage', label: 'Storage & units' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'other', label: 'Something else' },
];

const sent = computed(() => props.draft.sent || null);
const busy = ref(false);
const error = ref('');
const uploads = ref([]);

function onPick(event) {
  Array.from(event.target.files || [])
    .filter((file) => file && /^image\/(jpeg|png|webp)$/.test(file.type))
    .slice(0, 3 - uploads.value.length)
    .forEach((file) => uploads.value.push({ file, name: file.name, url: URL.createObjectURL(file), key: `${file.name}-${file.size}-${Date.now()}` }));
  event.target.value = '';
}
function removeUpload(i) {
  URL.revokeObjectURL(uploads.value[i].url);
  uploads.value.splice(i, 1);
}
onBeforeUnmount(() => uploads.value.forEach((f) => URL.revokeObjectURL(f.url)));

const form = reactive({
  title: props.draft.title || '',
  item_type: props.draft.item_type || 'other',
  quantity: props.draft.quantity || 1,
  width: props.draft.width ?? null,
  height: props.draft.height ?? null,
  depth: props.draft.depth ?? null,
  unit: props.draft.unit || 'cm',
  material: props.draft.material || '',
  fabric: props.draft.fabric || '',
  description: props.draft.description || '',
  photos: (props.draft.photos || []).map((p) => p.path),
  contact_name: props.user?.name || '',
  contact_phone: props.user?.phone || '',
  contact_email: props.user?.email || '',
});

async function submit() {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    const num = (v) => (v === '' || v === null || Number.isNaN(Number(v)) || Number(v) <= 0 ? null : Number(v));
    await props.send(props.message, {
      title: form.title.trim(),
      item_type: form.item_type,
      quantity: Math.max(1, Number(form.quantity) || 1),
      description: form.description.trim(),
      desired_width: num(form.width),
      desired_height: num(form.height),
      desired_depth: num(form.depth),
      dimension_unit: form.unit,
      preferred_material: form.material.trim() || null,
      preferred_fabric: form.fabric.trim() || null,
      photos: form.photos,
      files: uploads.value.map((f) => f.file),
      ...(props.signedIn ? {} : { contact_name: form.contact_name.trim(), contact_phone: form.contact_phone.trim(), contact_email: form.contact_email.trim() }),
    });
    uploads.value.forEach((f) => URL.revokeObjectURL(f.url));
    uploads.value = [];
  } catch (e) {
    const errors = e.payload?.errors;
    error.value = errors ? Object.values(errors).flat().join(' ') : (e.message || 'The request could not be sent. Please try again.');
  } finally {
    busy.value = false;
  }
}
</script>

<style>
.qd { margin: 8px 0 2px; border: 1px solid #dccdb9; border-radius: 14px; background: #fffdfa; overflow: hidden; }
.qd__head { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; padding: 10px 14px; background: #f6f2ec; border-bottom: 1px solid #eee4d6; }
.qd__badge { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 700; letter-spacing: 0.02em; text-transform: uppercase; color: #8a6a3e; }
.qd__hint { font-size: 12.5px; color: #6f7a82; }
.qd__sent { margin-left: auto; font-size: 12px; font-weight: 700; color: #2f7d4f; background: #e6f4ea; padding: 3px 10px; border-radius: 999px; }
.qd__summary { padding: 12px 14px; font-size: 13.5px; color: #3c474e; }
.qd__summary strong { display: block; color: #172430; margin-bottom: 4px; }
.qd__summary p { margin: 0 0 8px; white-space: pre-line; }
.qd__link { color: #bb976d; font-weight: 600; text-decoration: underline; font-size: 13px; }
.qd__form { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 12px; padding: 12px 14px 14px; }
.qd__field { display: flex; flex-direction: column; gap: 4px; min-width: 0; font-size: 12px; color: #6f7a82; }
.qd__field--wide { grid-column: 1 / -1; }
.qd__field--small { max-width: 120px; }
.qd__field input, .qd__field select, .qd__field textarea { width: 100%; min-width: 0; height: 36px; padding: 0 10px; border: 1px solid #dfe2e4; border-radius: 8px; background: #fff; font-size: 13.5px; color: #172430; font-family: inherit; }
.qd__field textarea { height: auto; padding: 8px 10px; line-height: 1.45; resize: vertical; }
.qd__field input:focus, .qd__field select:focus, .qd__field textarea:focus { outline: none; border-color: #bb976d; }
.qd__dims { display: grid; grid-template-columns: 1fr 1fr 1fr 72px; gap: 6px; }
.qd__photos { display: flex; flex-wrap: wrap; gap: 8px; }
.qd__photo { position: relative; width: 72px; height: 72px; border-radius: 10px; overflow: hidden; border: 2px solid #e3e5e6; cursor: pointer; opacity: 0.55; }
.qd__photo.is-on { border-color: #bb976d; opacity: 1; }
.qd__photo input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.qd__photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.qd__photo em { position: absolute; right: 4px; top: 4px; width: 18px; height: 18px; border-radius: 999px; background: #bb976d; color: #fff; font-size: 11px; font-style: normal; font-weight: 700; display: flex; align-items: center; justify-content: center; pointer-events: none; }
.qd__photo.is-upload { cursor: default; }
.qd__photo-remove { position: absolute; right: 3px; top: 3px; width: 20px; height: 20px; border-radius: 999px; border: 0; background: rgba(23, 36, 48, 0.85); color: #fff; font-size: 14px; line-height: 1; cursor: pointer; }
.qd__add { position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; width: 72px; height: 72px; border-radius: 10px; border: 2px dashed #cfc2ae; color: #8a6a3e; font-size: 11px; font-weight: 600; cursor: pointer; }
.qd__add:hover { border-color: #bb976d; background: #fbf4e8; }
.qd__add.is-disabled { opacity: 0.5; pointer-events: none; }
.qd__add input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.qd__photo-hint { display: block; margin-top: 4px; font-size: 11.5px; color: #8a9197; }
.qd__missing { margin: 0; font-size: 12.5px; color: #8a6a3e; background: #fbf4e8; border-radius: 8px; padding: 8px 10px; }
.qd__contact-title { font-size: 13px; font-weight: 700; color: #172430; margin-top: 4px; }
.qd__error { margin: 0; font-size: 12.5px; color: #b3261e; }
.qd__actions { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; font-size: 12px; color: #8a9197; }
.qd__send { display: inline-flex; align-items: center; gap: 7px; height: 40px; padding: 0 18px; border-radius: 999px; border: 0; background: #172430; color: #fff; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.qd__send:hover:not(:disabled) { background: #bb976d; }
.qd__send:disabled { opacity: 0.6; cursor: wait; }
@media (max-width: 520px) { .qd__form { grid-template-columns: 1fr; } .qd__field--small { max-width: none; } .qd__dims { grid-template-columns: 1fr 1fr; } }
.dark .qd { background: #1e2a35; border-color: #3c474e; }
.dark .qd__head { background: #22313d; border-color: #3c474e; }
.dark .qd__hint, .dark .qd__field, .dark .qd__actions { color: #aab3ba; }
.dark .qd__summary, .dark .qd__summary strong, .dark .qd__contact-title { color: #fff; }
.dark .qd__field input, .dark .qd__field select, .dark .qd__field textarea { background: #172430; border-color: #3c474e; color: #fff; }
.dark .qd__missing { background: #2a2f2a; }
</style>
